const ROOT = '/'
const ADMIN_BASE = '/admin/dashboard'
const CLIENT_PORTAL_BASE = '/client/portal'
const SETTINGS_BASE = '/app/settings'

import { SETTING_PAGES, SETTING_TOPICS } from '@/constants/settings/topics'

const INTEGRATIONS_BASE = '/integrations'
const APP_BASE = '/app'

const ADMIN = {
  ROOT: '',
  DASHBOARD: `${ADMIN_BASE}/dashboard`,
  UI_COMPONENTS: `${ADMIN_BASE}/ui-components`,
  CUSTOMERS: {
    CUSTOMER_LIST: `${ADMIN_BASE}/customers`,
    CUSTOMER_CREATE: `${ADMIN_BASE}/customers/create`,
    CUSTOMER_DETAIL: `${ADMIN_BASE}/customers/:uuid`,
    CUSTOMER_BOT_SETTINGS: `${ADMIN_BASE}/customers/:uuid/bot`,
    CUSTOMER_MAP: `${ADMIN_BASE}/customers/map`
  },
  VOICE: {
    PHONE_NUMBER_LIST: `${ADMIN_BASE}/voice/phone-numbers`,
    PHONE_NUMBER_DETAIL: `${ADMIN_BASE}/voice/phone-numbers/:uuid`,
    PHONE_NUMBER_CREATE: `${ADMIN_BASE}/voice/phone-numbers/create`,
    CALL_LIST: `${ADMIN_BASE}/voice/calls`,
    CALL_DETAIL: `${ADMIN_BASE}/voice/calls/:uuid`
  },
  EMAIL: {
    INBOUND_EMAIL_LIST: `${ADMIN_BASE}/inbound-emails`,
    INBOUND_EMAIL_DETAIL: `${ADMIN_BASE}/inbound-emails/:uuid`
  },
  DOCUMENTS: {
    DOCUMENTS_LIST: `${ADMIN_BASE}/documents/documents`,
    DOCUMENT_CREATE: `${ADMIN_BASE}/documents/documents/create`,
    DOCUMENT_DETAIL: `${ADMIN_BASE}/documents/documents/:uuid`,
    STALE_DOCUMENTS: `${ADMIN_BASE}/documents/stale`
  },
  PAYMENTS: {
    INVOICES_LIST: `${ADMIN_BASE}/payments/invoices`,
    INVOICES_EXPORT: `${ADMIN_BASE}/payments/invoices/export`
  },
  QUESTIONNAIRES: {
    QUESTIONNAIRE_LIST: `${ADMIN_BASE}/questionnaires`,
    QUESTIONNAIRE_DETAIL: `${ADMIN_BASE}/questionnaires/:uuid`
  },
  USERS: {
    USER_LIST: `${ADMIN_BASE}/users`,
    USER_DETAIL: `${ADMIN_BASE}/users/:uuid`
  },
  FEEDBACK: {
    FEEDBACK_LIST: `${ADMIN_BASE}/feedback`,
    FEEDBACK_DETAIL: `${ADMIN_BASE}/feedback/:uuid`
  },
  CHATS: {
    CHAT_LIST: `${ADMIN_BASE}/chats`,
    CHAT_SEARCH: `${ADMIN_BASE}/chats/search`,
    CHAT_DETAIL: `${ADMIN_BASE}/chats/:uuid`
  },
  WORD_TEMPLATES: {
    REVIEW: `${ADMIN_BASE}/word-templates/review`,
    REVIEW_DETAIL: `${ADMIN_BASE}/word-templates/review/:uuid`,
    VALIDATE: `${ADMIN_BASE}/word-templates/review/:uuid/validate`,
    PLACEHOLDERS: `${ADMIN_BASE}/word-templates/placeholders`,
    PLACEHOLDER_DETAIL: `${ADMIN_BASE}/word-templates/placeholders/:uuid`,
    EDITOR: `${ADMIN_BASE}/word-templates/:uuid/edit`
  },
  ANNOUNCEMENTS: {
    ANNOUNCEMENT_LIST: `${ADMIN_BASE}/announcements`,
    ANNOUNCEMENT_CREATE: `${ADMIN_BASE}/announcements/create`,
    ANNOUNCEMENT_DETAIL: `${ADMIN_BASE}/announcements/:uuid`
  },
  PDF_FORM_TEMPLATES: {
    PDF_FORM_TEMPLATES_LIST: `${ADMIN_BASE}/pdf-form-templates`,
    PDF_FORM_TEMPLATES_CREATE: `${ADMIN_BASE}/pdf-form-templates/create`,
    PDF_FORM_TEMPLATES_DETAIL: `${ADMIN_BASE}/pdf-form-templates/:uuid`
  }
}

const APP = {
  ROOT: '',
  EXPORT: `${APP_BASE}/${INTEGRATIONS_BASE}/:integration_name/export/:uuid`,
  OFFICE_EDITOR: `${APP_BASE}/office-editor/:documentUuid`,
  CASES: {
    CASE_OVERVIEW: `${APP_BASE}/cases`,
    CASE_CREATE: `${APP_BASE}/cases/create`,
    ORGANIZE_PAGES: `${APP_BASE}/cases/:caseId/organize-pages`,
    CASE_DETAIL: {
      ROOT: `${APP_BASE}/cases/:uuid`,
      OVERVIEW: `${APP_BASE}/cases/:uuid`,
      COMMUNICATION: `${APP_BASE}/cases/:uuid/communication`,
      ENTITIES: `${APP_BASE}/cases/:uuid/entities/:entityUuid?`,
      DOCUMENTS: `${APP_BASE}/cases/:uuid/documents`,
      APPOINTMENTS: `${APP_BASE}/cases/:uuid/appointments`,
      LAW_SPECIFIC_DATA: `${APP_BASE}/cases/:uuid/law-specific-data`,
      DOCUMENT_GENERATION: `${APP_BASE}/cases/:uuid/document-generation`,
      TIMELINE: `${APP_BASE}/cases/:uuid/timeline`,
      AI_CHAT: `${APP_BASE}/cases/:uuid/chat`,
      NOTES: `${APP_BASE}/cases/:uuid/notes`,
      MERGE_INTO: `${APP_BASE}/cases/:uuid/merge-into`
    },
    LEGACY: {
      ROOT: `${APP_BASE}/cases/legacy`,
      CASE_LIST: `${APP_BASE}/cases/legacy`,
      CASE_DETAIL: `${APP_BASE}/cases/legacy/:uuid`
    }
  },
  SETTINGS: {
    ROOT: SETTINGS_BASE,
    EMAILS: {
      JUPUS_EMAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.JUPUS_EMAIL}`,
      TEMPLATES: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.TEMPLATES}`,
      TEMPLATE_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.TEMPLATES}/:uuid`,
      DESIGN: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.DESIGN}`,
      SIGNATURES: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.SIGNATURES}`,
      NOTIFICATIONS: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.NOTIFICATIONS}`,
      PLACEHOLDERS: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.PLACEHOLDERS}`,
      DELIVERY_STATUS: `${SETTINGS_BASE}/${SETTING_TOPICS.EMAILS}/${SETTING_PAGES.EMAILS.DELIVERY_STATUS}`
    },
    APPOINTMENTS: {
      SETUP: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.SETUP}`,
      CALENDAR_LIST: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.CALENDAR_LIST}`,
      CALENDAR_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.CALENDAR_DETAIL}/:uuid`,
      SERVICE_LIST: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.SERVICE_LIST}`,
      SERVICE_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.SERVICE_DETAIL}/:uuid`,
      BASE_SETTINGS: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.BASE_SETTINGS}`,
      LINKS: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.LINKS}`,
      EMBED: `${SETTINGS_BASE}/${SETTING_TOPICS.APPOINTMENTS}/${SETTING_PAGES.APPOINTMENTS.EMBED}`
    },
    AI: {
      DOCUMENT_GENERATION_TEMPLATE_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.DOCUMENT_GENERATION_TEMPLATES}/:uuid`,
      DOCUMENT_GENERATION_TEMPLATES: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.DOCUMENT_GENERATION_TEMPLATES}`,
      DOCUMENT_GENERATION_TEMPLATES_BULK: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.DOCUMENT_GENERATION_TEMPLATES}/bulk`,
      PROMPT_TEMPLATE_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.PROMPT_TEMPLATES}/:uuid`,
      PROMPT_TEMPLATES: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.PROMPT_TEMPLATES}`,
      BASE_SETTINGS: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.BASE_SETTINGS}`,
      ENTITY_EXTRACTION: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.ENTITY_EXTRACTION}`,
      AUTO_COMMIT: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.AUTO_COMMIT}`,
      CASE_LIFECYCLE_WORKFLOWS: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.CASE_LIFECYCLE_WORKFLOWS}`,
      CASE_LIFECYCLE_WORKFLOWS_BASIC_SETTINGS: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.CASE_LIFECYCLE_WORKFLOWS_BASIC_SETTINGS}`,
      AI_AUTONOMY: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.AI_AUTONOMY}`,
      AI_NOTIFICATIONS: `${SETTINGS_BASE}/${SETTING_TOPICS.AI}/${SETTING_PAGES.AI.AI_NOTIFICATIONS}`
    },
    AI_PHONE_ANSWERING: {
      PHONE_NUMBERS: `${SETTINGS_BASE}/${SETTING_TOPICS.AI_PHONE_ANSWERING}/${SETTING_PAGES.AI_PHONE_ANSWERING.PHONE_NUMBERS}`,
      PHONE_NUMBERS_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.AI_PHONE_ANSWERING}/${SETTING_PAGES.AI_PHONE_ANSWERING.PHONE_NUMBERS}/:uuid`,
      PHONE_FORWARDING_RULES: `${SETTINGS_BASE}/${SETTING_TOPICS.AI_PHONE_ANSWERING}/${SETTING_PAGES.AI_PHONE_ANSWERING.PHONE_FORWARDING_RULES}`,
      PHONE_FORWARDING_RULE_EDIT: `${SETTINGS_BASE}/${SETTING_TOPICS.AI_PHONE_ANSWERING}/${SETTING_PAGES.AI_PHONE_ANSWERING.PHONE_FORWARDING_RULES}/edit/:uuid`
    },
    LAW_OFFICE_SOFTWARE: {
      ACTAPORT: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.ACTAPORT}`,
      ADVOLUX: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.ADVOLUX}`,
      ADVOWARE: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.ADVOWARE}`,
      JURNODES: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.JURNODES}`,
      KLEOS: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.KLEOS}`,
      KLEOS_OAUTH: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.KLEOS_OAUTH}`,
      LAWFIRM: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.LAWFIRM}`,
      RA_MICRO: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.RA_MICRO}`,
      DATEV: `${SETTINGS_BASE}/${SETTING_TOPICS.LAW_OFFICE_SOFTWARE}/${SETTING_PAGES.LAW_OFFICE_SOFTWARE.DATEV}`
    },
    INTEGRATIONS: {
      WEBHOOKS: `${SETTINGS_BASE}/${SETTING_TOPICS.INTEGRATIONS}/${SETTING_PAGES.INTEGRATIONS.WEBHOOKS}`,
      EMAIL_EGRESS: `${SETTINGS_BASE}/${SETTING_TOPICS.INTEGRATIONS}/${SETTING_PAGES.INTEGRATIONS.EMAIL_EGRESS}`,
      HUBSPOT: `${SETTINGS_BASE}/${SETTING_TOPICS.INTEGRATIONS}/${SETTING_PAGES.INTEGRATIONS.HUBSPOT}`,
      HIBP: `${SETTINGS_BASE}/${SETTING_TOPICS.INTEGRATIONS}/${SETTING_PAGES.INTEGRATIONS.HIBP}`,
      IUSTA: `${SETTINGS_BASE}/${SETTING_TOPICS.INTEGRATIONS}/${SETTING_PAGES.INTEGRATIONS.IUSTA}`
    },
    USER: {
      PROFILE: `${SETTINGS_BASE}/${SETTING_TOPICS.USER}/${SETTING_PAGES.USER.PROFILE}`,
      PASSWORD: `${SETTINGS_BASE}/${SETTING_TOPICS.USER}/${SETTING_PAGES.USER.PASSWORD}`
    },
    DOCUMENT_TEMPLATES: `${SETTINGS_BASE}/${SETTING_TOPICS.DOCUMENT_TEMPLATES}`,
    PDF_FORM_TEMPLATES: {
      PDF_FORM_TEMPLATES: `${SETTINGS_BASE}/${SETTING_TOPICS.PDF_FORM_TEMPLATES}`,
      PDF_FORM_TEMPLATES_CREATE: `${SETTINGS_BASE}/${SETTING_TOPICS.PDF_FORM_TEMPLATES}/create`,
      PDF_FORM_TEMPLATES_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.PDF_FORM_TEMPLATES}/:uuid`,
      PDF_FORM_TEMPLATES_EDIT: `${SETTINGS_BASE}/${SETTING_TOPICS.PDF_FORM_TEMPLATES}/:uuid/edit`
    },
    QUESTIONNAIRES: {
      QUESTIONNAIRE_LIST: `${SETTINGS_BASE}/${SETTING_TOPICS.QUESTIONNAIRES}`,
      QUESTIONNAIRE_DETAIL: `${SETTINGS_BASE}/${SETTING_TOPICS.QUESTIONNAIRES}/:uuid`
    },
    CLIENT_ONBOARDING: {
      ONBOARDING_PACKAGES: `${SETTINGS_BASE}/client-onboarding/${SETTING_TOPICS.ONBOARDING_PACKAGES}`,
      ONBOARDING_PACKAGE_DETAIL: `${SETTINGS_BASE}/client-onboarding/${SETTING_TOPICS.ONBOARDING_PACKAGES}/:uuid`,
      DOCUMENT_TEMPLATES: `${SETTINGS_BASE}/client-onboarding/${SETTING_TOPICS.DOCUMENT_TEMPLATES}`,
      QUESTIONNAIRES: `${SETTINGS_BASE}/client-onboarding/${SETTING_TOPICS.QUESTIONNAIRES}`,
      QUESTIONNAIRE_DETAIL: `${SETTINGS_BASE}/client-onboarding/${SETTING_TOPICS.QUESTIONNAIRES}/:uuid`
    },
    LAWFIRM: {
      LAWFIRMDATA: `${SETTINGS_BASE}/${SETTING_TOPICS.LAWFIRM}/${SETTING_PAGES.LAWFIRM.LAWFIRMDATA}`,
      BRANDING: `${SETTINGS_BASE}/${SETTING_TOPICS.LAWFIRM}/${SETTING_PAGES.LAWFIRM.BRANDING}`,
      TEAM: `${SETTINGS_BASE}/${SETTING_TOPICS.LAWFIRM}/${SETTING_PAGES.LAWFIRM.TEAM}`,
      TEAM_CREATE: `${SETTINGS_BASE}/${SETTING_TOPICS.LAWFIRM}/${SETTING_PAGES.LAWFIRM.TEAM}/create`,
      TEAM_EDIT: `${SETTINGS_BASE}/${SETTING_TOPICS.LAWFIRM}/${SETTING_PAGES.LAWFIRM.TEAM}/:uuid/edit`
    }
  },
  CALLS: {
    ROOT: `${APP_BASE}/calls`,
    CALL_DETAIL: `${APP_BASE}/calls/:uuid`
  },
  ACTIVITIES: {
    ROOT: `${APP_BASE}/activities`
  },
  GLOBAL_CHAT: {
    ROOT: `${APP_BASE}/global-chat`,
    SESSION_GROUP: `${APP_BASE}/global-chat/groups/:groupUuid`
    // TODO: implement with chat session link sharing
    // SESSION_DETAIL: `${APP_BASE}/global-chat/sessions/:sessionUuid`
  },
  ONBOARDING: {
    COUNTDOWN: `${APP_BASE}/onboarding/countdown`
  }
}

export const CLIENT_PORTAL = {
  HOME: `${CLIENT_PORTAL_BASE}`,
  QUESTIONNAIRE_DETAIL: `${CLIENT_PORTAL_BASE}/questionnaires/:uuid`,
  QUESTIONNAIRE_LEGACY_DETAIL: `${CLIENT_PORTAL_BASE}/questionnaire/:id`,
  QUESTIONNAIRE_PREVIEW: `${CLIENT_PORTAL_BASE}/questionnaires/preview/:uuid`,
  QUESTIONNAIRE_LEGACY_PREVIEW: `${CLIENT_PORTAL_BASE}/questionnaire/preview/:id`,
  EXTERNAL_QUESTIONNAIRE_DETAIL: `${CLIENT_PORTAL_BASE}/external/questionnaires/:uuid`,
  EXTERNAL_QUESTIONNAIRE_LEGACY_DETAIL: `${CLIENT_PORTAL_BASE}/external/questionnaire/:id`,
  EXTERNAL_QUESTIONNAIRE_REDIRECT: `${CLIENT_PORTAL_BASE}/external/questionnaires/:org_questionnaire_creation_token/:questionnaire_questionnaire_creation_token`,
  EXTERNAL_QUESTIONNAIRE_REDIRECT_SINGULAR: `${CLIENT_PORTAL_BASE}/external/questionnaire/:org_questionnaire_creation_token/:questionnaire_questionnaire_creation_token`,
  PDF_TEMPLATE_DETAIL: `${CLIENT_PORTAL_BASE}/pdf-templates/:form_token`,
  PDF_TEMPLATE_LEGACY_DETAIL: `${CLIENT_PORTAL_BASE}/document/:id`,
  EXTERNAL_PDF_TEMPLATE_DETAIL: `${CLIENT_PORTAL_BASE}/external/pdf-templates/:form_token`,
  PDF_FORM_SUBMISSION_DETAIL: `${CLIENT_PORTAL_BASE}/pdf-form-submissions/:submission_uuid`,
  EXTERNAL_COMPLETED: `${CLIENT_PORTAL_BASE}/external/questionnaire/completed`,
  EXTERNAL_PDF_COMPLETED: `${CLIENT_PORTAL_BASE}/external/pdf-templates/completed`,
  ONBOARDING_REDIRECT: `${CLIENT_PORTAL_BASE}/onboarding/:package_uuid`
}

export const ROUTES = {
  ROOT,
  ADMIN,
  APP,
  CLIENT_PORTAL
}

export const ROUTE_BASES = {
  ADMIN_BASE,
  CLIENT_PORTAL_BASE,
  APP_BASE,
  SETTINGS_BASE,
  INTEGRATIONS_BASE
}
